import { useState } from "react";

type SeoPage = {
  id: string;
  page: string;
  slug: string;
  title: string;
  description: string;
  keywords: string[];
  ogImage: string;
  indexed: boolean;
};

const initialPages: SeoPage[] = [
  { id:"s1", page:"Home", slug:"/", title:"NineSistersJewels – Handcrafted Gold & Kundan Jewellery", description:"Explore our stunning collection of handcrafted bridal and traditional jewellery.", keywords:["jewellery","gold","kundan","bridal"], ogImage:"https://placehold.co/1200x630/fde68a/92400e?text=OG+Home", indexed:true },
  { id:"s2", page:"Products", slug:"/products", title:"Shop Jewellery – Necklaces, Earrings, Bangles | NineSistersJewels", description:"Browse our wide range of gold, polki and kundan jewellery for all occasions.", keywords:["shop","necklace","earrings","bangles"], ogImage:"", indexed:true },
  { id:"s3", page:"Bridal Collection", slug:"/bridal", title:"Bridal Jewellery Sets 2025 – NineSistersJewels", description:"Find your perfect bridal jewellery set – polki, kundan, temple gold and more.", keywords:["bridal","wedding","set"], ogImage:"", indexed:true },
  { id:"s4", page:"About", slug:"/about", title:"About Us – NineSistersJewels", description:"Learn about our story, craftsmanship and values.", keywords:["about","story","artisan"], ogImage:"", indexed:false },
];

type ModalMode = "none"|"edit";

export function SeoPage() {
  const [pages, setPages] = useState<SeoPage[]>(initialPages);
  const [selected, setSelected] = useState<SeoPage|null>(null);
  const [form, setForm] = useState<SeoPage|null>(null);
  const [kwInput, setKwInput] = useState("");
  const [saved, setSaved] = useState(false);

  function openEdit(p: SeoPage) { setForm({...p}); setSelected(p); setKwInput(""); setSaved(false); }
  function closeModal() { setSelected(null); setForm(null); }
  function f<K extends keyof SeoPage>(k: K, v: any) { setForm(p=>p?({...p,[k]:v}):p); }

  function addKw() {
    const k = kwInput.trim().toLowerCase();
    if (k && form && !form.keywords.includes(k)) f("keywords", [...form.keywords, k]);
    setKwInput("");
  }
  function removeKw(kw: string) { if(form) f("keywords", form.keywords.filter(k=>k!==kw)); }

  function save() {
    if (!form) return;
    setPages(p=>p.map(pg=>pg.id===form.id?form:pg));
    setSaved(true);
    setTimeout(()=>setSaved(false),2000);
  }

  function toggleIndex(id: string) { setPages(p=>p.map(pg=>pg.id===id?{...pg,indexed:!pg.indexed}:pg)); }

  const titleScore = (t: string) => t.length >= 40 && t.length <= 60 ? "good" : t.length < 40 ? "short" : "long";
  const descScore = (d: string) => d.length >= 120 && d.length <= 160 ? "good" : d.length < 120 ? "short" : "long";
  const scoreColor = (s: string) => s==="good" ? "#16a34a" : s==="short" ? "#d97706" : "#dc2626";
  const scoreLabel = (s: string) => s==="good" ? "✓ Good" : s==="short" ? "⚠ Too short" : "⚠ Too long";

  return (
    <>
      <style>{`
        .seo-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:14px; }
        .seo-card { background:#fff; border-radius:14px; border:1.5px solid #f1f5f9; padding:18px 20px; cursor:pointer; transition:all .2s; }
        .seo-card:hover { border-color:var(--theme); box-shadow:0 4px 14px var(--theme-glow); transform:translateY(-2px); }
        .seo-card-head { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:10px; }
        .seo-page-name { font-size:15px; font-weight:700; color:#1e293b; }
        .seo-slug { font-family:monospace; font-size:11px; color:#94a3b8; background:#f8fafc; padding:2px 8px; border-radius:6px; }
        .seo-meta-title { font-size:13px; color:#1d4ed8; font-weight:600; margin-bottom:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .seo-meta-desc { font-size:12px; color:#64748b; line-height:1.5; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
        .seo-kw-row { display:flex; flex-wrap:wrap; gap:4px; margin-top:8px; }
        .seo-kw { background:var(--theme-light); color:var(--theme-dark); padding:2px 8px; border-radius:20px; font-size:11px; font-weight:600; }
        .seo-footer { display:flex; justify-content:space-between; align-items:center; margin-top:10px; padding-top:10px; border-top:1px solid #f8fafc; }
        .seo-indexed { font-size:11px; font-weight:700; padding:3px 10px; border-radius:20px; }
        .seo-score-bar { display:flex; gap:4px; margin-top:6px; }
        .seo-score-dot { width:10px; height:10px; border-radius:50%; }
        .ns-overlay { position:fixed; inset:0; background:rgba(15,23,42,.55); backdrop-filter:blur(4px); z-index:1000; display:flex; align-items:flex-start; justify-content:center; padding:24px 16px; overflow-y:auto; }
        .ns-modal { background:#fff; border-radius:20px; width:100%; max-width:640px; box-shadow:0 24px 60px rgba(0,0,0,.2); margin:auto; animation:mIn .25s ease; }
        @keyframes mIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        .ns-modal-head { display:flex; justify-content:space-between; align-items:center; padding:20px 24px; border-bottom:1px solid #f1f5f9; }
        .ns-modal-title { font-size:17px; font-weight:700; color:#1e293b; }
        .ns-modal-close { width:34px; height:34px; border-radius:9px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; font-size:16px; color:#64748b; }
        .ns-modal-body { padding:22px 24px; max-height:72vh; overflow-y:auto; }
        .ns-modal-foot { padding:16px 24px; border-top:1px solid #f1f5f9; display:flex; justify-content:flex-end; gap:10px; align-items:center; }
        .ns-field2 { margin-bottom:14px; }
        .ns-label2 { font-size:12px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.5px; display:block; margin-bottom:5px; }
        .ns-input2 { width:100%; padding:9px 12px; border:1.5px solid #e2e8f0; border-radius:10px; font-size:14px; outline:none; box-sizing:border-box; }
        .ns-input2:focus { border-color:var(--theme); box-shadow:0 0 0 3px var(--theme-glow); }
        .ns-textarea2 { width:100%; padding:9px 12px; border:1.5px solid #e2e8f0; border-radius:10px; font-size:14px; outline:none; box-sizing:border-box; resize:vertical; min-height:72px; }
        .ns-textarea2:focus { border-color:var(--theme); box-shadow:0 0 0 3px var(--theme-glow); }
        .ns-btn-sec { background:#f8fafc; color:#475569; border:1.5px solid #e2e8f0; border-radius:10px; padding:9px 16px; font-size:13px; font-weight:600; cursor:pointer; }
        .ns-btn-indigo { background:linear-gradient(135deg,var(--theme),#8b5cf6); color:#fff; border:none; border-radius:10px; padding:9px 20px; font-size:14px; font-weight:600; cursor:pointer; }
        .seo-char-hint { font-size:11px; margin-top:3px; font-weight:600; }
        .seo-kw-wrap { display:flex; flex-wrap:wrap; gap:5px; padding:8px; border:1.5px solid #e2e8f0; border-radius:10px; min-height:40px; }
        .seo-kw-rm { background:var(--theme-light); color:var(--theme-dark); border-radius:20px; padding:3px 10px; font-size:12px; font-weight:600; display:flex; align-items:center; gap:4px; }
        .seo-kw-rm button { background:none; border:none; cursor:pointer; color:var(--theme); font-size:13px; }
        .seo-kw-row2 { display:flex; gap:8px; margin-top:8px; }
        .seo-preview { background:#f8fafc; border-radius:12px; padding:14px 16px; margin:16px 0; border:1px solid #e2e8f0; }
        .seo-preview-title { color:#1a0dab; font-size:15px; font-weight:600; }
        .seo-preview-url { color:#188038; font-size:12px; margin:2px 0; }
        .seo-preview-desc { color:#4d5156; font-size:13px; line-height:1.4; }
        .seo-saved { color:#16a34a; font-size:13px; font-weight:600; }
        .seo-toggle-row { display:flex; align-items:center; gap:10px; }
        .seo-tgl { position:relative; width:40px; height:22px; }
        .seo-tgl input { opacity:0; width:0; height:0; }
        .seo-tgl-sl { position:absolute; cursor:pointer; inset:0; background:#e2e8f0; border-radius:22px; transition:.3s; }
        .seo-tgl-sl:before { content:''; position:absolute; width:16px; height:16px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:.3s; }
        input:checked + .seo-tgl-sl { background:var(--theme); }
        input:checked + .seo-tgl-sl:before { transform:translateX(18px); }
      `}</style>
      <div>
        <div className="seo-health-box">
          <div className="seo-health-title">🔍 SEO Health Overview</div>
          <div className="seo-health-stats">{pages.filter(p=>p.indexed).length}/{pages.length} pages indexed · {pages.filter(p=>p.keywords.length>0).length} pages with keywords</div>
        </div>
        <div className="seo-grid">
          {pages.map(p => {
            const ts = titleScore(p.title), ds = descScore(p.description);
            return (
              <div key={p.id} className="seo-card" onClick={()=>openEdit(p)}>
                <div className="seo-card-head">
                  <div>
                    <div className="seo-page-name">{p.page}</div>
                    <div className="seo-slug">{p.slug}</div>
                  </div>
                  <span className={`seo-indexed ${p.indexed ? "seo-indexed-active" : "seo-indexed-inactive"}`}>
                    {p.indexed?"Indexed":"No-index"}
                  </span>
                </div>
                <div className="seo-meta-title">🔷 {p.title || "—"}</div>
                <div className="seo-meta-desc">{p.description || "No description set"}</div>
                <div className="seo-kw-row">{p.keywords.slice(0,4).map(k=><span key={k} className="seo-kw">{k}</span>)}{p.keywords.length>4&&<span className="seo-kw">+{p.keywords.length-4}</span>}</div>
                <div className="seo-score-bar">
                  <span className="seo-score-hint" style={{color:scoreColor(ts)}}>Title: {scoreLabel(ts)}</span>
                  <span className="seo-score-sep">·</span>
                  <span className="seo-score-hint" style={{color:scoreColor(ds)}}>Desc: {scoreLabel(ds)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {form && selected && (
        <div className="ns-overlay" onClick={e=>{if(e.target===e.currentTarget)closeModal();}}>
          <div className="ns-modal">
            <div className="ns-modal-head">
              <span className="ns-modal-title">🔍 SEO – {form.page}</span>
              <button className="ns-modal-close" onClick={closeModal} type="button">✕</button>
            </div>
            <div className="ns-modal-body">
              <div className="seo-preview">
                <div className="seo-preview-title">{form.title || "Page Title"}</div>
                <div className="seo-preview-url">ninesistersjewels.com{form.slug}</div>
                <div className="seo-preview-desc">{form.description || "Meta description will appear here..."}</div>
              </div>
              <div className="ns-field2">
                <label className="ns-label2">Meta Title</label>
                <input className="ns-input2" value={form.title} onChange={e=>f("title",e.target.value)} placeholder="Page title for search engines" />
                <div className="seo-char-hint seo-score-hint" style={{color:scoreColor(titleScore(form.title))}}>
                  {form.title.length} chars · {scoreLabel(titleScore(form.title))} (ideal: 40–60)
                </div>
              </div>
              <div className="ns-field2">
                <label className="ns-label2">Meta Description</label>
                <textarea className="ns-textarea2" value={form.description} onChange={e=>f("description",e.target.value)} placeholder="Short description for search results..." />
                <div className="seo-char-hint seo-score-hint" style={{color:scoreColor(descScore(form.description))}}>
                  {form.description.length} chars · {scoreLabel(descScore(form.description))} (ideal: 120–160)
                </div>
              </div>
              <div className="ns-field2">
                <label className="ns-label2">Keywords</label>
                <div className="seo-kw-wrap">
                  {form.keywords.map(k=><span key={k} className="seo-kw-rm">{k}<button onClick={()=>removeKw(k)} type="button">×</button></span>)}
                </div>
                <div className="seo-kw-row2">
                  <input className="ns-input2" value={kwInput} onChange={e=>setKwInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addKw()} placeholder="Add keyword..." className="seo-kw-input" />
                  <button className="ns-btn-sec" onClick={addKw} type="button">Add</button>
                </div>
              </div>
              <div className="ns-field2">
                <label className="ns-label2">OG Image URL</label>
                <input className="ns-input2" value={form.ogImage} onChange={e=>f("ogImage",e.target.value)} placeholder="https://... (1200×630 recommended)" />
              </div>
              <div className="ns-field2">
                <label className="ns-label2">Indexing</label>
                <div className="seo-toggle-row">
                  <label className="seo-tgl">
                    <input type="checkbox" checked={form.indexed} onChange={e=>f("indexed",e.target.checked)} />
                    <span className="seo-tgl-sl"></span>
                  </label>
                  <span className="seo-indexed-label">{form.indexed?"Allow search engines to index this page":"No-index (hidden from search)"}</span>
                </div>
              </div>
            </div>
            <div className="ns-modal-foot">
              {saved && <span className="seo-saved">✓ Saved!</span>}
              <button className="ns-btn-sec" onClick={closeModal} type="button">Close</button>
              <button className="ns-btn-indigo" onClick={save} type="button">Save SEO Settings</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
